/** Every admin read and write. Only three of the five tabs have an endpoint behind them. */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import type {
  ActivityRoleAssignment,
  Entitlement,
  Role,
  RoleAssignment,
  ScopeRef,
} from "@nebulize/sdk";

import { api } from "@/lib/api";

export type { ActivityRoleAssignment, Entitlement, Role, RoleAssignment, ScopeRef };

export function useHeldRoles(subjectId: string | undefined) {
  return useQuery({
    queryKey: ["admin", "account", subjectId, "roles"],
    enabled: subjectId !== undefined && subjectId !== "",
    retry: false,
    queryFn: () => api.get<RoleAssignment[]>(`/v1/accounts/${subjectId ?? ""}/roles`),
  });
}

export interface GrantInput {
  readonly subject_id: string;
  readonly role: Role;
  readonly scope: ScopeRef;
}

export function useGrantRole(subjectId: string | undefined) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: GrantInput) => api.post<RoleAssignment>("/v1/role-assignments", input),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ["admin", "account", subjectId, "roles"] });
    },
  });
}

export function useRevokeAssignment(subjectId: string | undefined) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (assignmentId: string) =>
      api.delete<undefined>(`/v1/role-assignments/${assignmentId}`),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ["admin", "account", subjectId, "roles"] });
    },
  });
}

export function useMyEntitlement() {
  return useQuery({
    queryKey: ["admin", "entitlement", "me"],
    queryFn: () => api.get<Entitlement>("/v1/accounts/me/entitlement"),
  });
}

export interface ActivityRoleChange {
  readonly assignmentId: string;
  readonly role: string;
}

export function useChangeActivityRole() {
  return useMutation({
    mutationFn: (input: ActivityRoleChange) =>
      api.patch<ActivityRoleAssignment>(`/v1/activity-role-assignments/${input.assignmentId}`, {
        role: input.role,
        catch_all: true,
      }),
  });
}

export function useRemoveActivityRole() {
  return useMutation({
    mutationFn: (assignmentId: string) =>
      api.delete<undefined>(`/v1/activity-role-assignments/${assignmentId}`),
  });
}
